import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO, { SEOConfigs } from '../components/SEO';
import { 
  TrendingUp, Signal, CheckCircle, ArrowRight, 
  Bell, Target, Shield, Clock, Zap, ChevronRight,
  Users, Star, BarChart3, Rocket, Sparkles, Activity
} from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SimpleLandingPage = () => {
  const [signalStats, setSignalStats] = useState({
    activeSignals: 0,
    activePositions: 0,
    todaySignals: 0,
    tp1Rate: 0, // Will be calculated from real data
    tp2Rate: 0, // Will be calculated from real data
    totalUsers: 1500
  });
  const [recentSignals, setRecentSignals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch live signals, active positions, and performance data
        const [signalsRes, positionsRes, performanceRes] = await Promise.all([
          axios.get(`${API_URL}/api/signals/live`),
          axios.get(`${API_URL}/api/signals/positions/active`).catch(() => ({ data: { positions: [] } })),
          axios.get(`${API_URL}/api/signals/performance`).catch(() => ({ data: null }))
        ]);
        
        const signals = signalsRes.data?.signals || signalsRes.data || [];
        const positions = positionsRes.data?.positions || [];
        const performance = performanceRes.data;
        
        // Get position symbols to exclude
        const positionSymbols = positions.map(p => p.sembol);
        
        // Filter for NEW opportunities only:
        // 1. Recent (within last 6 hours)
        // 2. No open position for this symbol
        // 3. Not closed/stopped
        const SIX_HOURS = 6 * 60 * 60 * 1000;
        const now = Date.now();
        
        const newOpportunities = signals.filter(s => {
          // Must be BUY or SELL
          if (s.action !== 'BUY' && s.action !== 'SELL') return false;
          // Skip demo signals
          if (s.source === 'demo') return false;
          // Skip closed signals
          if (s.outcome_status === 'stopped' || s.outcome_status === 'success' || s.outcome_status === 'full_success') return false;
          // Skip if there's an open position for this symbol
          if (positionSymbols.includes(s.symbol)) return false;
          // Skip if signal is too old (> 6 hours)
          const signalTime = new Date(s.timestamp || s.created_at).getTime();
          if (now - signalTime > SIX_HOURS) return false;
          
          return true;
        });
        
        // Use performance API for accurate rates
        let tp1Rate = 0;
        let tp2Rate = 0;
        
        if (performance && performance.total_signals > 0) {
          // Use real performance data from API
          tp1Rate = Math.round(performance.tp1_rate || 0);
          tp2Rate = Math.round(performance.tp2_rate || 0);
        }
        
        setSignalStats({
          activeSignals: newOpportunities.length, // Only NEW opportunities (not positions)
          activePositions: positions.length,
          todaySignals: signals.filter(s => {
            const signalDate = new Date(s.timestamp || s.created_at);
            const today = new Date();
            return signalDate.toDateString() === today.toDateString();
          }).length,
          tp1Rate: tp1Rate,
          tp2Rate: tp2Rate,
          totalUsers: 1500
        });
        
        // Get 3 recent signals WITH complete data (tp2 and stop_loss) for preview
        // Landing page should only show clean, complete signals
        const completeSignals = signals.filter(s => 
          (s.tp2 || s.target_price) && 
          s.stop_loss && 
          (s.entry_price || s.price)
        );
        setRecentSignals(completeSignals.slice(0, 3));
      } catch (err) {
        console.error('Stats fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);

  const benefits = [
    { 
      icon: <Signal className="h-6 w-6" />, 
      title: 'Anlık Sinyaller', 
      desc: 'AL-SAT sinyallerini gerçek zamanlı alın',
      color: '#00E599'
    },
    { 
      icon: <Target className="h-6 w-6" />, 
      title: 'Hedef & Stop', 
      desc: 'Her sinyalde net giriş, hedef ve zarar kes seviyeleri',
      color: '#8B5CF6'
    },
    { 
      icon: <Bell className="h-6 w-6" />, 
      title: 'Bildirimler', 
      desc: 'Telegram, web push ve e-posta ile anında haber alın',
      color: '#F59E0B'
    },
    { 
      icon: <Shield className="h-6 w-6" />, 
      title: 'Risk Yönetimi', 
      desc: 'Otomatik pozisyon boyutlandırma ve zarar limiti',
      color: '#EC4899'
    }
  ];

  const howItWorks = [
    { step: '1', title: 'Ücretsiz Kayıt', desc: '30 saniyede hesap oluştur' },
    { step: '2', title: 'Sinyalleri Takip Et', desc: 'AL-SAT sinyallerini anında gör' },
    { step: '3', title: 'Trade Yap', desc: 'Sinyallere göre işlem yap' }
  ];

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: price > 100 ? 0 : 2
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO {...SEOConfigs.home} />
      
      {/* Hero Section - Simple & Signal Focused */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-green/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-brand-green/8 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-500/8 rounded-full blur-[100px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Main Message */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-green/10 border border-brand-green/20 mb-6">
                <Sparkles className="h-4 w-4 text-brand-green" />
                <span className="text-brand-green text-sm font-medium">
                  AI Destekli Karar Motoru
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4 text-white leading-[1.15]">
                Yapay Zekâ ile
                <span className="block text-brand-green">Ne Zaman İşlem Yapacağını Bil</span>
              </h1>
              
              {/* Powerful tagline */}
              <p className="text-xl md:text-2xl text-slate-300 font-medium mb-8">
                AI Destekli Kripto Al-Sat Sinyalleri
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-brand-green/25 w-full sm:w-auto">
                    Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/performans">
                  <Button size="lg" variant="outline" className="border-slate-700 text-white hover:bg-slate-800 px-8 py-6 text-lg rounded-xl w-full sm:w-auto">
                    <BarChart3 className="mr-2 h-5 w-5" /> Performansı İncele
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            {/* Right - EXAMPLE Signals Preview - DESKTOP ONLY */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <Card className="border-slate-700/50 bg-slate-900/80 backdrop-blur-sm overflow-hidden">
                <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 px-4 py-3 border-b border-slate-700/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full" />
                      <span className="text-sm font-semibold text-white">Örnek Sinyal</span>
                    </div>
                    <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                      ÖRNEK
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-700/50">
                    {/* Static Example Signal Cards - NOT live data */}
                    {[
                      { id: 'example1', symbol: 'BTC-USDT', action: 'BUY', entry_price: 97500, tp2: 101500, stop_loss: 95500, outcome_status: 'active' },
                      { id: 'example2', symbol: 'ETH-USDT', action: 'BUY', entry_price: 3450, tp2: 3650, stop_loss: 3350, outcome_status: 'partial_success', outcome_emoji: '🟡', outcome_label: 'Kısmi Başarı' },
                      { id: 'example3', symbol: 'SOL-USDT', action: 'SELL', entry_price: 185, tp2: 165, stop_loss: 195, outcome_status: 'success', outcome_emoji: '🟢', outcome_label: 'Başarılı', pnl_percent: 8.1 },
                    ].map((signal, idx) => (
                      <div key={signal.id || idx} className="p-4 hover:bg-slate-800/30 transition-colors">
                        {/* Row 1: Action + Symbol + Status */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs font-bold ${
                              signal.action === 'BUY' ? 'bg-green-900/50 text-green-400 border-green-700/50' :
                              signal.action === 'EXIT' ? 'bg-amber-900/50 text-amber-400 border-amber-700/50' :
                              'bg-red-900/50 text-red-400 border-red-700/50'
                            }`}>
                              {signal.action === 'BUY' ? '🟢 AL' : signal.action === 'EXIT' ? '🔔 ÇIKIŞ' : '🔴 SAT'}
                            </Badge>
                            <span className="font-bold text-white">{signal.symbol}</span>
                          </div>
                          {/* Outcome Badge */}
                          {signal.outcome_status && signal.outcome_status !== 'active' && (
                            <Badge className={`text-[10px] ${
                              signal.outcome_status === 'full_success' ? 'bg-emerald-500/20 text-emerald-400' :
                              signal.outcome_status === 'success' ? 'bg-green-500/20 text-green-400' :
                              signal.outcome_status === 'partial_success' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>
                              {signal.outcome_emoji} {signal.outcome_label}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Row 2: Entry, Target, Stop - Clean grid */}
                        <div className="grid grid-cols-3 gap-3 text-xs">
                          <div>
                            <span className="text-slate-500 block mb-0.5">Giriş</span>
                            <span className="text-white font-semibold">{formatPrice(signal.entry_price || signal.price)}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Hedef</span>
                            <span className="text-green-400 font-semibold">{formatPrice(signal.tp2 || signal.target_price)}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Stop</span>
                            <span className="text-red-400 font-semibold">{formatPrice(signal.stop_loss)}</span>
                          </div>
                        </div>
                        
                        {/* PnL for closed signals */}
                        {signal.pnl_percent !== undefined && signal.pnl_percent !== null && (
                          <div className="mt-2 pt-2 border-t border-slate-700/30 text-xs">
                            <span className={`font-medium ${signal.pnl_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {signal.pnl_percent >= 0 ? '✅' : '❌'} {signal.pnl_percent >= 0 ? '+' : ''}{signal.pnl_percent.toFixed(1)}% kapandı
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Footer note - IMPORTANT: Explains this is an example */}
                  <div className="px-4 py-2.5 bg-slate-800/30 border-t border-slate-700/30">
                    <p className="text-[10px] text-slate-400 text-center">
                      Bu bir örnek sinyaldir. Canlı sinyaller üye girişi sonrası gösterilir.
                    </p>
                  </div>
                  
                  <Link to="/signals" className="block">
                    <div className="px-4 py-3 bg-slate-800/50 hover:bg-slate-700/50 transition-colors text-center border-t border-slate-700/30">
                      <span className="text-sm text-brand-green font-medium">
                        Canlı Sinyalleri Gör <ChevronRight className="inline h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          </div>
          
          {/* MOBILE ONLY - Example Signal Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="lg:hidden mt-8"
          >
            <Card className="border-slate-700/50 bg-slate-900/80 backdrop-blur-sm overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 px-4 py-3 border-b border-slate-700/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-400 rounded-full" />
                    <span className="text-sm font-semibold text-white">Örnek Sinyal</span>
                  </div>
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                    ÖRNEK
                  </Badge>
                </div>
              </div>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-700/50">
                  {[
                    { id: 'mob1', symbol: 'BTC-USDT', action: 'BUY', entry_price: 97500, tp2: 101500, stop_loss: 95500 },
                    { id: 'mob2', symbol: 'SOL-USDT', action: 'SELL', entry_price: 185, tp2: 165, stop_loss: 195, pnl_percent: 8.1 },
                  ].map((signal, idx) => (
                    <div key={signal.id} className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs font-bold ${
                            signal.action === 'BUY' ? 'bg-green-900/50 text-green-400 border-green-700/50' :
                            'bg-red-900/50 text-red-400 border-red-700/50'
                          }`}>
                            {signal.action === 'BUY' ? '🟢 AL' : '🔴 SAT'}
                          </Badge>
                          <span className="font-bold text-white">{signal.symbol}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div>
                          <span className="text-slate-500 block mb-0.5">Giriş</span>
                          <span className="text-white font-semibold">{formatPrice(signal.entry_price)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block mb-0.5">Hedef</span>
                          <span className="text-green-400 font-semibold">{formatPrice(signal.tp2)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block mb-0.5">Stop</span>
                          <span className="text-red-400 font-semibold">{formatPrice(signal.stop_loss)}</span>
                        </div>
                      </div>
                      {signal.pnl_percent && (
                        <div className="mt-2 pt-2 border-t border-slate-700/30 text-xs">
                          <span className="text-green-400 font-medium">✅ +{signal.pnl_percent}% kapandı</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="px-4 py-2.5 bg-slate-800/30 border-t border-slate-700/30">
                  <p className="text-[10px] text-slate-400 text-center">
                    Bu bir örnek sinyaldir. Canlı sinyaller üye girişi sonrası gösterilir.
                  </p>
                </div>
                <Link to="/signals" className="block">
                  <div className="px-4 py-3 bg-slate-800/50 hover:bg-slate-700/50 transition-colors text-center border-t border-slate-700/30">
                    <span className="text-sm text-brand-green font-medium">
                      Canlı Sinyalleri Gör <ChevronRight className="inline h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Trust Block - Şeffaflık & Güvenlik */}
      <section className="py-8 border-b border-slate-800/30">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10 text-sm">
            <Link to="/performans" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                <BarChart3 className="h-4 w-4 text-emerald-400" />
              </div>
              <span>Sinyaller geçmiş sonuçlarla ölçülür</span>
            </Link>
            <Link to="/performans" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                <Activity className="h-4 w-4 text-blue-400" />
              </div>
              <span>Tüm sonuçları şeffaf şekilde görebilirsin</span>
            </Link>
            <Link to="/legal/risk" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                <Shield className="h-4 w-4 text-purple-400" />
              </div>
              <span>Borsana bağlanmayız • API anahtarı istemeyiz</span>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works - Super Simple */}
      <section className="py-16 border-y border-slate-800/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Nasıl Çalışır?
            </h2>
            <p className="text-slate-400">3 adımda trading sinyalleri almaya başla</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {howItWorks.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="relative text-center"
              >
                <div className="w-12 h-12 rounded-full bg-brand-green/20 border border-brand-green/30 flex items-center justify-center mx-auto mb-4">
                  <span className="text-brand-green font-bold text-lg">{item.step}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
                
                {idx < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-px bg-gradient-to-r from-brand-green/30 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="mb-4 px-3 py-1 bg-purple-500/10 text-purple-400 border-purple-500/20">
              <Zap className="h-3 w-3 mr-1" /> Özellikler
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Her Sinyalde Ne Alırsınız?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-slate-800 bg-slate-900/50 hover:bg-slate-800/50 transition-all">
                  <CardContent className="p-5">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                      style={{ backgroundColor: benefit.color + '20', color: benefit.color }}
                    >
                      {benefit.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                    <p className="text-sm text-slate-400">{benefit.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Signal Source - Borsamex Automated Systems */}
      <section className="py-16 bg-gradient-to-br from-slate-900/80 via-purple-900/10 to-slate-900/80">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-purple-500/30 bg-slate-900/80 overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                      <Zap className="h-10 w-10 text-white" />
                    </div>
                  </div>
                  <div className="text-center md:text-left">
                    <Badge className="mb-3 bg-purple-500/20 text-purple-400 border-purple-500/30">
                      Borsamex Otomatik Sistem
                    </Badge>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      7/24 Çalışan Akıllı Algoritmalar
                    </h3>
                    <p className="text-slate-400 mb-6">
                      Borsamex'in kendi geliştirdiği algoritmik trading botları, piyasaları sürekli tarayarak 
                      size en uygun AL-SAT fırsatlarını sunar. RSI, MACD, EMA gibi teknik göstergeleri 
                      kullanan bu sistemler, insan hatasını minimize eder.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-brand-green" />
                        <span className="text-slate-300">Otomatik Risk Yönetimi</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-brand-green" />
                        <span className="text-slate-300">Gerçek Zamanlı Analiz</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-brand-green" />
                        <span className="text-slate-300">Backtest Onaylı</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Kripto Sinyal Rehberleri - Internal Linking for SEO */}
      <section className="py-16 bg-slate-900/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Kripto Sinyalleri Hakkında
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Sistemin nasıl çalıştığını ve farklı sinyal türlerini keşfedin
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-5 max-w-6xl mx-auto">
            {/* AI Kripto Sinyalleri - Hub */}
            <Link to="/ai-kripto-sinyalleri" className="group">
              <Card className="h-full border-purple-500/20 bg-slate-900/50 hover:border-purple-500/40 hover:bg-slate-800/50 transition-all">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Sparkles className="h-5 w-5 text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 group-hover:text-purple-400 transition-colors">AI Sinyalleri</h3>
                  <p className="text-xs text-slate-500">Tüm pariteler</p>
                </CardContent>
              </Card>
            </Link>

            {/* BTC Sinyalleri */}
            <Link to="/btc-sinyalleri" className="group">
              <Card className="h-full border-amber-500/20 bg-slate-900/50 hover:border-amber-500/40 hover:bg-slate-800/50 transition-all">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <span className="text-amber-400 text-lg">₿</span>
                  </div>
                  <h3 className="font-semibold text-white mb-1 group-hover:text-amber-400 transition-colors">BTC Sinyalleri</h3>
                  <p className="text-xs text-slate-500">Bitcoin fırsatları</p>
                </CardContent>
              </Card>
            </Link>

            {/* ETH Sinyalleri */}
            <Link to="/eth-sinyalleri" className="group">
              <Card className="h-full border-blue-500/20 bg-slate-900/50 hover:border-blue-500/40 hover:bg-slate-800/50 transition-all">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <span className="text-blue-400 text-lg">Ξ</span>
                  </div>
                  <h3 className="font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors">ETH Sinyalleri</h3>
                  <p className="text-xs text-slate-500">Ethereum fırsatları</p>
                </CardContent>
              </Card>
            </Link>

            {/* LONG Sinyaller */}
            <Link to="/long-sinyaller" className="group">
              <Card className="h-full border-green-500/20 bg-slate-900/50 hover:border-green-500/40 hover:bg-slate-800/50 transition-all">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <TrendingUp className="h-5 w-5 text-green-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 group-hover:text-green-400 transition-colors">LONG Sinyaller</h3>
                  <p className="text-xs text-slate-500">Yükseliş yönlü</p>
                </CardContent>
              </Card>
            </Link>

            {/* SHORT Sinyaller */}
            <Link to="/short-sinyaller" className="group">
              <Card className="h-full border-red-500/20 bg-slate-900/50 hover:border-red-500/40 hover:bg-slate-800/50 transition-all">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <TrendingUp className="h-5 w-5 text-red-400 rotate-180" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 group-hover:text-red-400 transition-colors">SHORT Sinyaller</h3>
                  <p className="text-xs text-slate-500">Düşüş yönlü</p>
                </CardContent>
              </Card>
            </Link>
          </div>
          
          {/* Secondary Links */}
          <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm">
            <Link to="/nasil-calisir" className="text-slate-400 hover:text-white transition-colors flex items-center gap-1">
              <ChevronRight className="h-4 w-4" /> Nasıl Çalışır?
            </Link>
            <Link to="/performans" className="text-slate-400 hover:text-white transition-colors flex items-center gap-1">
              <ChevronRight className="h-4 w-4" /> Performans Verileri
            </Link>
            <Link to="/sss" className="text-slate-400 hover:text-white transition-colors flex items-center gap-1">
              <ChevronRight className="h-4 w-4" /> Sık Sorulan Sorular
            </Link>
          </div>
        </div>
      </section>

      {/* Mini FAQ - Halk Dili */}
      <section className="py-16 bg-slate-900/30">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Merak Edilenler
            </h2>
            <p className="text-slate-400">En çok sorulan 5 soru</p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "Borsamex borsama bağlanıyor mu?",
                a: "Hayır. Borsamex sadece sinyal verir. Borsana bağlanmıyoruz, API anahtarı istemiyoruz, paranıza dokunmuyoruz. İşlemi kendi hesabında kendin yaparsın."
              },
              {
                q: "Sinyaller yatırım tavsiyesi mi?",
                a: "Hayır. Sinyaller teknik analize dayalı fırsat bildirimleridir, yatırım tavsiyesi değildir. Her işlemde kendi araştırmanı yap ve risk yönetimini uygula."
              },
              {
                q: "Telegram'dan bildirim geliyor mu?",
                a: "Evet. Pro üyelik ile anlık Telegram bildirimi alabilirsin. Sinyal üretildiği anda telefonuna mesaj düşer."
              },
              {
                q: "TP ve Stop ne demek?",
                a: "TP (Take Profit) = Kar al seviyesi. Stop (Stop Loss) = Zarar kes seviyesi. Her sinyalde bu seviyeler belirlenir, böylece ne zaman çıkacağını önceden bilirsin."
              },
              {
                q: "Ücretsiz kullanım var mı?",
                a: "Evet. Ücretsiz plan ile tüm canlı sinyalleri görebilir, geçmiş performansı inceleyebilirsin. Anlık bildirimler için Pro üyelik gerekir."
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                viewport={{ once: true }}
              >
                <Card className="border-slate-700/50 bg-slate-900/50 hover:bg-slate-800/50 transition-colors">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                      <span className="text-brand-green">?</span>
                      {item.q}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed pl-5">{item.a}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/sss" className="text-slate-400 hover:text-white transition-colors text-sm">
              Tüm sorular için SSS sayfasına göz at →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Şimdi Başla, <span className="text-brand-green">Ücretsiz</span>
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Borsamex'e ücretsiz kaydol ve profesyonel trading sinyallerini takip etmeye başla.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/pricing">
                <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-10 py-6 text-lg rounded-xl shadow-lg shadow-brand-green/25">
                  Ücretsiz Hesap Oluştur <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            
            {/* Trust Elements */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-brand-green" />
                <span>Kredi kartı gerekmez</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-brand-green" />
                <span>30 saniyede kayıt</span>
              </div>
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-brand-green" />
                <span>Anlık Bildirimler</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pro section removed for MVP */}
    </div>
  );
};

export default SimpleLandingPage;
